import { v4 as uuid } from "uuid";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Node } from "@iventis/domain-model/model/node";
import React, { FunctionComponent, useMemo, useState } from "react";
import { DialogDescription, inlineTextIconMargin, muiInputFormsCSS, StyledInputLabel, styled } from "@iventis/styles";
import { findNode, createBreadcrumb } from "@iventis/tree-browser";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { Content } from "@iventis/translations/content/typed-content";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FormWizardTemplate, LocationPrivacy, SaveInLocation, Stage, FormWizardTitle } from "@iventis/components";
import { TextField } from "@mui/material";
import { useInputAutoFocus, canEdit } from "@iventis/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PermissionsEditModalComponent } from "@iventis/permissions/src/permissions-edit-modal";
import { libraryNodeTypeFontawesome } from "./library-nodes";
import { getLibraryIconParameters } from "./mapping-library.helpers";
import { MaxNodeNameLength } from "./validation";
import { LibraryMoveDialog } from "./library-move-dialog";

export const CreateNodeComponent: FunctionComponent<{
    onClose: () => void;
    onConfirm: (node: Node, newTree?: Node, newParentId?: string) => Promise<void>;
    parentId: string;
    nodeTypeToCreate: NodeType;
    title: string;
    subtitle?: string;
    fieldLabel: string;
    description?: string;
    tree: Node;
}> = ({ onClose, onConfirm, parentId, nodeTypeToCreate, title, subtitle, fieldLabel, description, tree }) => {
    const [currentTree, setCurrentTree] = useState<Node>(tree);
    const [name, setName] = useState<string>("");
    const [currentParentId, setCurrentParentId] = useState<string>(parentId);

    const translate = useIventisTranslate();
    const buttonTextTranslated = {
        loading: translate(Content.library.create_node.creating, {
            nodeName: `${translate(Content.library.node_names[nodeTypeToCreate])}`,
        }),
        create: translate(Content.library.create_node.title, {
            nodeName: `${translate(Content.library.node_names[nodeTypeToCreate])}`,
        }),
    };

    const [loading, setLoading] = useState<boolean>();
    const [changingLocation, setChangingLocation] = useState<boolean>(false);

    const create = async () => {
        const node: Node = {
            parentId: currentParentId,
            id: uuid(),
            name,
            type: nodeTypeToCreate,
            childNodes: [],
            favourite: false,
            authorisation: Authorisation.Admin,
            sourceId: null,
            childCount: 0,
            createdAt: new Date(),
            lastUpdatedAt: new Date(),
        } as Node;
        node.sourceId = node.id;
        setLoading(true);
        if (tree.id !== currentTree.id) {
            await onConfirm(node, currentTree, currentParentId);
        } else {
            await onConfirm(node);
        }
        setLoading(false);
        onClose();
    };

    const nameIsTooLong = name.length > MaxNodeNameLength;

    const nodeIcon = useMemo(() => <FontAwesomeIcon key="node-icon" icon={libraryNodeTypeFontawesome[nodeTypeToCreate]} />, []);

    const onInput = useInputAutoFocus();

    const parentNode = findNode([currentTree], currentParentId);
    const hasPermission = useMemo(() => canEdit(parentNode?.authorisation), [parentNode]);
    const [permissionsEditOpen, setPermissionsEditOpen] = useState(false);

    const stages: Stage[] = [
        {
            primaryButtonText: (
                <>
                    {loading ? <FontAwesomeIcon style={{ marginRight: inlineTextIconMargin }} icon={{ prefix: "fas", iconName: "circle-notch" }} className="fa-spin" /> : ""}{" "}
                    {buttonTextTranslated.create}
                </>
            ),
            primaryButtonCallback: () => create(),
            isValid: hasPermission && name.length > 0 && !nameIsTooLong && !loading,
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: onClose }],
        },
    ];

    const creationLocationPrivacy = useMemo(() => {
        if (currentTree.type === NodeType.PersonalMappingLibrary) {
            return LocationPrivacy.Private;
        }
        if (currentTree.type === NodeType.MappingLibrary) {
            return LocationPrivacy.Everyone;
        }
        return LocationPrivacy.Specific;
    }, [parentNode, currentTree]);

    const crumbs = createBreadcrumb(parentNode.id, currentTree);

    const helperText = nameIsTooLong && translate(Content.library.create_node.maxInputChars);

    const getTranslatedHierarchyName = (node: Node): string => {
        if (node.type === NodeType.PersonalMappingLibrary || node.type === NodeType.MappingLibrary) {
            return translate(Content.library.node_names[node.type]);
        }

        return node.name;
    };

    return (
        <>
            <FormWizardTemplate title={<FormWizardTitle title={[nodeIcon, title]} header={subtitle} />} stages={stages} currentStage={0} isSubmitting={false}>
                <StyledContent>
                    {description !== undefined && <DialogDescription>{description}</DialogDescription>}
                    <StyledInputLabel>{fieldLabel}</StyledInputLabel>
                    <TextField
                        inputRef={onInput}
                        variant="outlined"
                        error={nameIsTooLong}
                        helperText={helperText}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        inputProps={{ "data-cy": "node-name-input" }}
                    />
                    <StyledInputLabel>{translate(Content.library.create_node.saveInFolder)}</StyledInputLabel>
                    <SaveInLocation
                        hierarchyNames={crumbs.map((c) => getTranslatedHierarchyName(c as Node))}
                        privacy={creationLocationPrivacy}
                        icon={<FontAwesomeIcon icon={getLibraryIconParameters(currentTree)} />}
                        onViewPermissions={() => setPermissionsEditOpen(true)}
                        onChangeLocation={() => setChangingLocation(true)}
                    />
                    {parentNode && <PermissionsEditModalComponent open={permissionsEditOpen} node={parentNode} close={() => setPermissionsEditOpen(false)} />}
                </StyledContent>
            </FormWizardTemplate>
            <LibraryMoveDialog
                isOpen={changingLocation}
                closeModal={() => setChangingLocation(false)}
                treeId={tree.id}
                onConfirm={(newTree, _, newParentId) => {
                    setCurrentTree(newTree);
                    setCurrentParentId(newParentId);
                }}
                onConfirmSwitchLibrary={(newTree, newParentId) => {
                    setCurrentTree(newTree);
                    setCurrentParentId(newParentId);
                }}
                canSwitchLibrary={tree?.type === NodeType.PersonalMappingLibrary}
            />
        </>
    );
};

const StyledContent = styled.div`
    ${muiInputFormsCSS};
    .MuiFormControl-root {
        width: 100%;
    }
`;
